/**
 * Трекинг сайта и личного кабинета для раздела «Статистика».
 *
 * Считаем людей, а не клики: анонимам выдаём долгоживущий идентификатор
 * устройства, авторизованным бэкенд подставит user_id из токена. События
 * копятся в очереди и уходят пачкой — чтобы переход по страницам не стоил
 * запроса на каждый клик. Трекинг никогда не мешает интерфейсу: любые
 * ошибки сети молча проглатываются.
 */
import { USER_TOKEN_KEY } from './authTokens';

const BASE = process.env.REACT_APP_ADMIN_API_URL ?? 'http://localhost:3001';
const ANON_KEY = 'analytics_anon_id';
/** Пачку отправляем не чаще, чем раз в это время */
const FLUSH_MS = 2000;
const MAX_QUEUE = 25;

export type AnalyticsSource = 'site' | 'panel';

interface QueuedEvent {
  event: string;
  source: AnalyticsSource;
  anon_id: string;
  meta?: Record<string, unknown>;
}

function anonId(): string {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    // Приватный режим / отключённое хранилище: событие всё равно посчитаем
    return 'no-storage';
  }
}

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function flush(useBeacon = false): void {
  if (!queue.length) return;
  const events = queue;
  queue = [];
  if (timer) { clearTimeout(timer); timer = null; }

  const url = `${BASE}/api/analytics/events`;
  const body = JSON.stringify({ events });

  // При закрытии вкладки fetch отменяется — sendBeacon доставит событие
  if (useBeacon && navigator.sendBeacon) {
    try { navigator.sendBeacon(url, new Blob([body], { type: 'application/json' })); } catch { /* ignore */ }
    return;
  }

  const token = localStorage.getItem(USER_TOKEN_KEY);
  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body,
    keepalive: true,
  }).catch(() => { /* статистика не важнее работы сайта */ });
}

/** Поставить событие в очередь (уйдёт пачкой в ближайшие пару секунд). */
export function track(event: string, source: AnalyticsSource, meta?: Record<string, unknown>): void {
  queue.push({ event, source, anon_id: anonId(), meta });
  if (queue.length >= MAX_QUEUE) { flush(); return; }
  if (!timer) timer = setTimeout(() => flush(), FLUSH_MS);
}

/** То же, но без ожидания пачки — для событий прямо перед уходом со страницы. */
export function trackNow(event: string, source: AnalyticsSource, meta?: Record<string, unknown>): void {
  queue.push({ event, source, anon_id: anonId(), meta });
  flush();
}

/**
 * Просмотр страницы. Путь нормализуем (числовые id → :id), иначе разрез
 * по разделам превратится в тысячу уникальных строк.
 */
export function trackPageView(pathname: string): void {
  const path = pathname.replace(/\/\d+(?=\/|$)/g, '/:id').slice(0, 120) || '/';
  const source: AnalyticsSource = path.startsWith('/panel') ? 'panel' : 'site';
  track('page_view', source, { path });
}

/** Ссылка «Скачать лаунчер»: сам факт скачивания считает бэкенд при редиректе. */
export function launcherDownloadUrl(): string {
  return `${BASE}/api/analytics/download?aid=${encodeURIComponent(anonId())}`;
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
}
